import { useEffect, useMemo, useState } from "react";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import DetailsCard from "@/components/DetailsCard";
import Select from "@/components/Select";
import Switch from "@/components/Switch";
import TextField from "@/components/TextField";
import type { UpsertTaskDepartmentNotificationDestinationInput } from "../mutations";
import type { SlackChannel, TaskDepartmentNotificationDestination } from "../queries";

const INBOX_EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

export type DestinationDraft = Pick<
  TaskDepartmentNotificationDestination,
  | "workdayDepartmentName"
  | "email"
  | "emailEnabled"
  | "emailLastError"
  | "slackChannelId"
  | "slackChannelName"
  | "slackEnabled"
  | "slackLastError"
> & { id?: string };

function isValidInboxEmail(value: string): boolean {
  return INBOX_EMAIL_REGEX.test(value.trim());
}

function channelLabel(name: string | null | undefined, id: string): string {
  return name ? `#${name}` : id;
}

function slackChannelTestId(departmentName: string): string {
  return `slack-channel-${departmentName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")}`;
}

type DestinationEditorProps = {
  destination: DestinationDraft;
  saving: boolean;
  slackChannels?: SlackChannel[];
  slackChannelsError?: boolean;
  slackChannelsLoading?: boolean;
  slackDestinationsUnavailable?: boolean;
  onSave: (input: UpsertTaskDepartmentNotificationDestinationInput) => Promise<boolean>;
};

export default function DestinationEditor({
  destination,
  saving,
  slackChannels = [],
  slackChannelsError = false,
  slackChannelsLoading = false,
  slackDestinationsUnavailable = false,
  onSave
}: DestinationEditorProps) {
  const [email, setEmail] = useState(destination.email ?? "");
  const [emailEnabled, setEmailEnabled] = useState(destination.emailEnabled);
  const [slackEnabled, setSlackEnabled] = useState(destination.slackEnabled);
  const [slackChannelId, setSlackChannelId] = useState(destination.slackChannelId ?? "");
  const [error, setError] = useState<string | undefined>();
  const [slackError, setSlackError] = useState<string | undefined>();
  const [toggleSaving, setToggleSaving] = useState(false);

  useEffect(() => {
    setEmail(destination.email ?? "");
    setEmailEnabled(destination.emailEnabled);
    setError(undefined);
  }, [destination.email, destination.emailEnabled]);

  useEffect(() => {
    setSlackEnabled(destination.slackEnabled);
    setSlackChannelId(destination.slackChannelId ?? "");
    setSlackError(undefined);
  }, [destination.slackChannelId, destination.slackEnabled]);

  const storedEmail = destination.email ?? "";
  const storedSlackChannelId = destination.slackChannelId ?? "";
  const departmentName = destination.workdayDepartmentName;
  const showInboxField = emailEnabled;
  const showInboxActions = showInboxField && email.trim() !== storedEmail.trim();
  const showStaleBanner = Boolean(destination.emailLastError) && emailEnabled;
  const showSlackField = slackEnabled;
  const showSlackActions = showSlackField && slackChannelId !== storedSlackChannelId;
  const showSlackStaleBanner = Boolean(destination.slackLastError) && slackEnabled;
  const controlsDisabled = saving || toggleSaving;

  const slackChannelOptions = useMemo(() => {
    const options = slackChannels.map((channel) => ({
      value: channel.id,
      displayText: channelLabel(channel.name, channel.id)
    }));
    if (storedSlackChannelId && !options.some((option) => option.value === storedSlackChannelId)) {
      options.unshift({
        value: storedSlackChannelId,
        displayText: channelLabel(destination.slackChannelName, storedSlackChannelId)
      });
    }
    return options;
  }, [destination.slackChannelName, slackChannels, storedSlackChannelId]);

  const slackChannelNameById = useMemo(() => {
    const names = new Map(slackChannels.map((channel) => [channel.id, channel.name]));
    if (storedSlackChannelId && destination.slackChannelName) {
      names.set(storedSlackChannelId, destination.slackChannelName);
    }
    return names;
  }, [destination.slackChannelName, slackChannels, storedSlackChannelId]);

  async function persistEmail(nextEnabled: boolean, nextEmail: string) {
    if (nextEnabled) {
      const trimmed = nextEmail.trim();
      if (!trimmed) {
        setError("Enter an email address.");
        return false;
      }
      if (!isValidInboxEmail(trimmed)) {
        setError("Enter a valid email.");
        return false;
      }
      setError(undefined);
      return onSave({
        workdayDepartmentName: departmentName,
        emailEnabled: true,
        email: trimmed
      });
    }

    setError(undefined);
    return onSave({
      workdayDepartmentName: departmentName,
      emailEnabled: false
    });
  }

  async function persistSlack(nextEnabled: boolean, nextChannelId: string) {
    if (nextEnabled) {
      if (!nextChannelId) {
        setSlackError("Choose a Slack channel.");
        return false;
      }
      setSlackError(undefined);
      return onSave({
        workdayDepartmentName: departmentName,
        emailEnabled: destination.emailEnabled,
        slackEnabled: true,
        slackChannelId: nextChannelId,
        slackChannelName: slackChannelNameById.get(nextChannelId) ?? destination.slackChannelName
      });
    }

    setSlackError(undefined);
    return onSave({
      workdayDepartmentName: departmentName,
      emailEnabled: destination.emailEnabled,
      slackEnabled: false
    });
  }

  async function persistEmailToggle(nextEnabled: boolean) {
    const previous = emailEnabled;
    setEmailEnabled(nextEnabled);
    if (nextEnabled && !storedEmail.trim()) {
      return;
    }

    setToggleSaving(true);
    try {
      const ok = await persistEmail(nextEnabled, storedEmail);
      if (!ok) {
        setEmailEnabled(previous);
      }
    } catch {
      setEmailEnabled(previous);
    } finally {
      setToggleSaving(false);
    }
  }

  async function persistSlackToggle(nextEnabled: boolean) {
    const previous = slackEnabled;
    setSlackEnabled(nextEnabled);
    if (nextEnabled && !storedSlackChannelId) {
      return;
    }

    setToggleSaving(true);
    try {
      const ok = await persistSlack(nextEnabled, storedSlackChannelId);
      if (!ok) {
        setSlackEnabled(previous);
      }
    } catch {
      setSlackEnabled(previous);
    } finally {
      setToggleSaving(false);
    }
  }

  return (
    <section aria-label={`${departmentName} notification preferences`}>
      <DetailsCard heading={departmentName}>
        <div className="flex flex-col gap-6">
          {showStaleBanner ? (
            <Banner
              variant="error"
              hideSecondaryDismiss
              description="The last email to this inbox did not send."
              subText={destination.emailLastError ?? undefined}
            />
          ) : null}
          <Switch
            headline="Send Email"
            aria-label={`Send Email, ${departmentName}`}
            selected={emailEnabled}
            disabled={controlsDisabled}
            onChange={() => {
              void persistEmailToggle(!emailEnabled);
            }}
          />
          {showInboxField ? (
            <>
              <TextField
                className="w-full"
                label={`Email Address, ${departmentName}`}
                type="email"
                value={email}
                disabled={controlsDisabled}
                error={Boolean(error)}
                errorText={error}
                onChangeText={(value) => {
                  setEmail(value);
                  if (error) {
                    setError(undefined);
                  }
                }}
              />
              {showInboxActions ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="filled"
                    loading={saving}
                    ariaLabel={`Save ${departmentName} inbox`}
                    onClick={() => {
                      void persistEmail(true, email);
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="text"
                    disabled={controlsDisabled}
                    ariaLabel={`Cancel ${departmentName} inbox`}
                    onClick={() => {
                      setEmail(storedEmail);
                      setError(undefined);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : null}
            </>
          ) : null}
          {showSlackStaleBanner ? (
            <Banner
              variant="error"
              hideSecondaryDismiss
              description="The last Slack message to this channel did not send."
              subText={destination.slackLastError ?? undefined}
            />
          ) : null}
          {slackChannelsError && slackEnabled ? (
            <Banner
              variant="error"
              hideSecondaryDismiss
              description="Slack channels not loaded."
              subText="The Member 360 bot cannot list channels yet. Ask tech to give it permission to list channels."
            />
          ) : null}
          <Switch
            headline="Send Slack"
            aria-label={`Send Slack, ${departmentName}`}
            selected={slackEnabled}
            disabled={controlsDisabled || slackDestinationsUnavailable}
            onChange={() => {
              void persistSlackToggle(!slackEnabled);
            }}
          />
          {showSlackField ? (
            <>
              <Select
                className="w-full"
                data-testid={slackChannelTestId(departmentName)}
                label={`Slack Channel, ${departmentName}`}
                placeholder="Choose a channel"
                searchable
                options={slackChannelOptions}
                value={slackChannelId}
                disabled={controlsDisabled || slackChannelsLoading}
                error={Boolean(slackError)}
                errorText={slackError}
                supportingText={
                  slackChannelsLoading
                    ? "Loading channels."
                    : "Every channel the Member 360 bot is in, including other teams' private channels. Invite the bot first, then pick it here."
                }
                onSelect={(value) => {
                  setSlackChannelId(value);
                  if (slackError) {
                    setSlackError(undefined);
                  }
                }}
              />
              {showSlackActions ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="filled"
                    loading={saving}
                    ariaLabel={`Save ${departmentName} Slack channel`}
                    onClick={() => {
                      void persistSlack(true, slackChannelId);
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="text"
                    disabled={controlsDisabled}
                    ariaLabel={`Cancel ${departmentName} Slack channel`}
                    onClick={() => {
                      setSlackChannelId(storedSlackChannelId);
                      setSlackError(undefined);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </DetailsCard>
    </section>
  );
}
